/**
 * Persist and restore skin flow navigation state so it survives page refresh.
 * State is stored in sessionStorage keyed by session_id so that when the user
 * refreshes (e.g. on skin-results, skin-routine, product-recommendations),
 * we can restore the data that was originally passed via location.state.
 *
 * Multiple users: no conflict between users because
 * - sessionStorage is per-origin per-tab (each tab has its own storage).
 * - Keys include session_id (Hair_flow_state_<session_id>), so different
 *   sessions never share the same key, even in the same tab.
 * - On restore we validate that stored state.sessionId matches the requested
 *   sessionId, so we never return another session's data.
 */

import { HAIR_FLOW_STATE_PREFIX } from '../config/hairApp';

const KEY_PREFIX = HAIR_FLOW_STATE_PREFIX;
const TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

function normalizeSessionId(sessionId) {
  if (sessionId == null) return null;
  const s = String(sessionId).trim();
  return s || null;
}

function storageKey(sessionId) {
  const sid = normalizeSessionId(sessionId);
  return sid ? KEY_PREFIX + sid : null;
}

/**
 * Save flow state for the given session (e.g. when a page has location.state).
 * Only saves when sessionId is valid; state is stored under a key that includes
 * sessionId so multiple users/sessions never conflict.
 * @param {string} sessionId
 * @param {object} state - same shape as location.state (capturedImage, skinResults, userData, etc.)
 */
export function saveSkinFlowState(sessionId, state) {
  const sid = normalizeSessionId(sessionId);
  const key = storageKey(sid);
  if (!key || !state || typeof state !== 'object') return;
  try {
    const payload = {
      state: { ...state, sessionId: state.sessionId ?? sid },
      ts: Date.now(),
      sid
    };
    sessionStorage.setItem(key, JSON.stringify(payload));
  } catch (e) {
    // quota or disabled storage
  }
}

/**
 * Get flow state for the given session (for restore after refresh).
 * Returns null if missing, expired, or if stored state belongs to a different
 * session (so multiple users never see each other's data).
 * @param {string} sessionId
 * @returns {object|null} state object or null
 */
export function getSkinFlowState(sessionId) {
  const sid = normalizeSessionId(sessionId);
  const key = storageKey(sid);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { state, ts, sid: storedSid } = JSON.parse(raw);
    if (!state || typeof state !== 'object') return null;
    if (Date.now() - (ts || 0) > TTL_MS) return null;
    const stateSessionId = state.sessionId != null ? String(state.sessionId).trim() : storedSid;
    if (stateSessionId && sid && stateSessionId !== sid) return null;
    return state;
  } catch (e) {
    return null;
  }
}
